import { MAX_TITLE_LENGTH, MIN_TITLE_LENGTH } from '@opengov/stories-shared/constants';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useMemo, useState } from 'react';

import { publicationView as publicationViewLocale } from '../../../../locale/stories.en.locale.json';
import { IPublicationModalMessages } from '../../../../types';
import {
  useUpdatePublicationSection,
  useUpdatePublicationUpdatedAt
} from '../PublicationView/hooks/usePublicationMutations';

import { styles } from './UpdatePublicationSectionModal.styles';

export interface IUpdatePublicationSectionModalProps {
  isAdmin: boolean;
  userId: number;
  entityId: number;
  publicationModalMessages: IPublicationModalMessages;
  selectedSectionId: string;
  publicationId: string;
  selectedSectionName: string;
  onClose(): void;
}

export const UpdatePublicationSectionModal: React.FC<IUpdatePublicationSectionModalProps> = (props) => {
  const {
    publicationModalMessages,
    userId,
    onClose,
    isAdmin,
    entityId,
    selectedSectionId,
    selectedSectionName,
    publicationId
  } = props;
  const [sectionTitle, setSectionTitle] = useState(selectedSectionName);
  const trimmedSectionName = useMemo(() => sectionTitle.trim(), [sectionTitle]);

  // Section rename hook lives here now (no parent callback). Mirrors
  // the retired wrapper — chained updatePublicationUpdatedAt on success, errors
  // swallowed so close-on-confirm is unchanged (hook toasts the error).
  const updatePublicationSectionMutation = useUpdatePublicationSection(
    { title: publicationViewLocale.somethingWentWrongError, body: publicationViewLocale.failedToUpdatePublicationSection },
    publicationViewLocale.noSectionSameName
  );
  const updateTimestampMutation = useUpdatePublicationUpdatedAt();

  const handleConfirmUpdatePublicationSection = async () => {
    onClose();
    try {
      await updatePublicationSectionMutation.mutateAsync({
        selectedSectionId: selectedSectionId,
        entityId: entityId,
        title: trimmedSectionName
      });
      updateTimestampMutation.mutate({ publicationId: publicationId, userId });
    } catch {
      // Error toast emitted by useUpdatePublicationSection.
    }
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="update-section-dialog-title">
      <DialogTitle id="update-section-dialog-title" sx={styles.dialogTitle}>
        {publicationModalMessages.editSection}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box data-test="update-section-form">
          <Box>
            <Box sx={styles.labelRow}>
              <Typography id="update-section-name-label" variant="body2" sx={styles.formLabel}>
                {publicationModalMessages.sectionName}
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {sectionTitle.length}/{MAX_TITLE_LENGTH}
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder={publicationModalMessages.enterNamePlaceholder}
              value={sectionTitle}
              onChange={(e) => setSectionTitle(e.target.value)}
              sx={styles.textField}
              inputProps={{ 'data-test': 'section-title-input', maxLength: MAX_TITLE_LENGTH, className: 'no-format', 'aria-labelledby': 'update-section-name-label' }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button variant="outlined" color="secondary" size="small" onClick={onClose} data-test="cancel-update-publication-button">
          {publicationModalMessages.cancel}
        </Button>
        {isAdmin && (
          <Button
            variant="contained"
            size="small"
            color="primary"
            disabled={trimmedSectionName.length < MIN_TITLE_LENGTH || trimmedSectionName.length > MAX_TITLE_LENGTH}
            onClick={() => {
              handleConfirmUpdatePublicationSection();
            }}
            data-test="update-section-button"
          >
            {publicationModalMessages.save}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
